/**
 * 생산 화면 — 생산 라인이 도면·재단표를 보고 패널을 만들고 완료를 남기는 곳 (금액 없음)
 * 왼쪽: 작업지시된 작업 (작업일·출고 예정 순, 지연 빨강) / 오른쪽: 패널 체크 · 재단표 · 도면 · 메모·기록
 * 생산 직원(PROD)은 로그인하면 이 화면만 보임
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  BoardRow, WorkDetail, WorkLog, PanelItem, workBoard, workDetail, panelAction, workLog, addWorkMemo, completeJob, planImageUrl, errMsg,
} from '../utils/api';
import { MATERIAL_SPECS } from '../utils/materialSpecs';
import { PANEL, d8, byPanelNo } from './WorkBoard';
import { btn, inp, area, chip, th, td, num, C } from './ui';

const today = () => new Date(Date.now() + 9 * 3600e3).toISOString().slice(0, 10).replace(/-/g, '');
const fmt = (s: string | null) => (s || '').replace('T', ' ').slice(5, 16);
const material = (cd: string) => (MATERIAL_SPECS as Record<string, { label: string }>)[cd]?.label ?? cd ?? '';
/** 작업 기록 이름 */
const ACT: Record<string, string> = {
  ORDER: '작업지시', MADE: '생산 완료', STOCK: '입고', OUT: '출고', DONE: '설치', TODO: '생산 전으로', NOTE: '메모', JOB_DONE: '전체 생산 완료',
  SHIP_LOAD: '차에 싣기', SHIP_DONE: '도착·설치', SHIP_CANCEL: '배차 취소',
};
const KIND_ORDER = ['벽', '칸막이', '천장', '바닥'];
const late = (r: { DUE_DT: string | null; JOB_STATE: string | null }) => !!r.DUE_DT && r.DUE_DT < today() && r.JOB_STATE !== 'OUT' && r.JOB_STATE !== 'DONE';

/** 도면 그림 (로그인 토큰이 필요해 blob으로 받아 보여 줌) */
const PlanImage: React.FC<{ no: string; has?: boolean }> = ({ no, has }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!has) return;
    let u: string | null = null, live = true;
    planImageUrl(no).then(x => { u = x; if (live) setUrl(x); else URL.revokeObjectURL(x); }).catch(e => live && setErr(errMsg(e)));
    return () => { live = false; if (u) URL.revokeObjectURL(u); };
  }, [no, has]);
  if (!has) return <div style={{ color: C.mute, padding: 16 }}>도면 그림이 없습니다 — 사무실에서 도면을 열고 「작업지시」를 다시 내리면 생깁니다.</div>;
  if (err) return <div style={{ color: C.red, padding: 16 }}>⚠ {err}</div>;
  if (!url) return <div style={{ color: C.mute, padding: 16 }}>불러오는 중…</div>;
  return (
    <div>
      <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: C.blue }}>크게 보기 (새 창)</a>
      <img src={url} alt="도면" style={{ width: '100%', marginTop: 8, borderRadius: 12, border: `1px solid ${C.line}`, background: '#fff' }} />
    </div>
  );
};

const Detail: React.FC<{ no: string; onChanged: () => void }> = ({ no, onChanged }) => {
  const [d, setD] = useState<WorkDetail | null>(null);
  const [log, setLog] = useState<WorkLog[]>([]);
  const [tab, setTab] = useState<'panels' | 'cut' | 'plan' | 'log'>('panels');
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // 누르고 있는 패널 (두 번 눌림 막기)
  const [memo, setMemo] = useState('');
  const [loc, setLoc] = useState(() => { try { return localStorage.getItem('pd_last_loc') || ''; } catch { return ''; } });
  const load = () => Promise.all([workDetail(no), workLog(no)])
    .then(([a, b]) => { setD(a.data); setLog(b.data); setErr(null); }).catch(e => setErr(errMsg(e)));
  useEffect(() => { setD(null); setOk(null); setErr(null); load(); }, [no]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (f: () => Promise<unknown>, msg?: string) => {
    try { await f(); await load(); onChanged(); setOk(msg ?? null); setErr(null); } catch (e) { setErr(errMsg(e)); setOk(null); }
  };
  // 종류별 묶음 (벽 → 칸막이 → 천장 → 바닥)
  const groups = useMemo(() => {
    const m = new Map<string, PanelItem[]>();
    [...(d?.panels ?? [])].sort((a, b) => byPanelNo(a.PANEL_NO, b.PANEL_NO)).forEach(p => m.set(p.KIND, [...(m.get(p.KIND) ?? []), p]));
    return Array.from(m).sort((a, b) => (KIND_ORDER.indexOf(a[0]) + 1 || 9) - (KIND_ORDER.indexOf(b[0]) + 1 || 9));
  }, [d]);
  // 재단표: 같은 종류·폭·길이·결합끼리 수량
  const cut = useMemo(() => {
    const m = new Map<string, { kind: string; size: number; length: number; joint: string; nos: string[]; made: number }>();
    groups.forEach(([, ps]) => ps.forEach(p => {
      const k = `${p.KIND}|${p.SIZE}|${p.LENGTH}|${p.JOINT || ''}`;
      const r = m.get(k) ?? { kind: p.KIND, size: p.SIZE, length: p.LENGTH, joint: p.JOINT || '', nos: [], made: 0 };
      r.nos.push(p.PANEL_NO); if (p.STATE !== 'TODO') r.made++;
      m.set(k, r);
    }));
    return Array.from(m.values());
  }, [groups]);

  if (!d) return <div style={{ padding: 24, color: err ? C.red : C.mute }}>{err ?? '불러오는 중…'}</div>;
  const e = d.estimate;
  const todo = d.panels.filter(p => p.STATE === 'TODO').length, made = d.panels.filter(p => p.STATE === 'MADE').length;
  const doneN = d.panels.length - todo;
  /** 패널 누름: 생산 전 → 생산 완료 / 생산 완료 → (확인 후) 생산 전으로 / 입고 이후는 그대로 */
  const tap = (p: PanelItem) => {
    if (busy) return;
    if (p.STATE === 'TODO') { setBusy(p.PANEL_NO); run(() => panelAction(no, [p.PANEL_NO], 'made'), `${p.PANEL_NO} 생산 완료`).finally(() => setBusy(null)); return; }
    if (p.STATE === 'MADE' && window.confirm(`${p.PANEL_NO}을(를) '생산 전'으로 되돌릴까요?`)) {
      setBusy(p.PANEL_NO); run(() => panelAction(no, [p.PANEL_NO], 'reset'), `${p.PANEL_NO} 생산 전으로`).finally(() => setBusy(null));
    }
  };
  const tabs: [typeof tab, string][] = [['panels', `패널 ${doneN}/${d.panels.length}`], ['cut', `재단표 ${cut.length}종`], ['plan', '도면'], ['log', `메모·기록 ${log.length}`]];
  return (
    <div style={{ padding: 16, maxWidth: 1100 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: 20, letterSpacing: -0.3 }}>{e.SITE_NM || '(현장명 없음)'}</h2>
          <span style={{ color: C.mute, fontSize: 13 }}>{e.ESTI_NO}</span>
        </div>
        <div style={{ fontSize: 14, color: C.sub, marginTop: 6, lineHeight: 1.7 }}>
          {material(e.MATERIAL_CD)} · {e.PANEL_THICK}T · 바닥 {e.HORIZONTAL} × {e.VERTICAL}{e.WALLHEIGHT ? ` · 벽 높이 ${e.WALLHEIGHT}` : ''}<br />
          작업일 <b>{d8(e.WORK_DT) || '-'}</b> · 출고 예정 <b style={{ color: late(e) ? C.red : undefined }}>{d8(e.DUE_DT) || '-'}{late(e) ? ' (지연)' : ''}</b> · 담당 <b>{e.WORK_MGR || '-'}</b>
        </div>
        {e.WORK_MEMO && <div style={{ marginTop: 8, padding: '10px 12px', background: C.orangeWeak, borderRadius: 10, fontSize: 14, color: '#333D4B', whiteSpace: 'pre-wrap' }}>📝 {e.WORK_MEMO}</div>}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
          <div style={{ flex: 1, minWidth: 160, height: 8, borderRadius: 4, background: C.fill, overflow: 'hidden' }}>
            <div style={{ width: `${d.panels.length ? (doneN / d.panels.length) * 100 : 0}%`, height: '100%', background: todo ? C.orange : C.green }} />
          </div>
          <span style={{ fontSize: 13, color: C.sub }}>생산 <b>{doneN}</b> / {d.panels.length}</span>
          <button style={btn(C.blue, !!todo)} disabled={!todo} onClick={() => {
            if (window.confirm(`남은 ${todo}장을 모두 '생산 완료'로 바꿀까요?`)) run(() => completeJob(no), `이 작업 전체 생산 완료 (${todo}장)`);
          }}>{todo ? `이 작업 전체 완료 (남은 ${todo}장)` : '✓ 생산 모두 완료'}</button>
        </div>
        {(made > 0) && <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
          <input value={loc} onChange={ev => setLoc(ev.target.value)} placeholder="보관 위치 (예: A-3)" style={{ ...inp, width: 160 }} />
          <button style={btn(C.blue, false)} onClick={() => {
            try { localStorage.setItem('pd_last_loc', loc); } catch { /* 저장소 없음 */ }
            run(() => panelAction(no, d.panels.filter(p => p.STATE === 'MADE').map(p => p.PANEL_NO), 'in', loc), `생산 완료 ${made}장 입고`);
          }}>생산 완료 {made}장 입고</button>
        </div>}
        {ok && <div style={{ color: C.green, fontSize: 14, fontWeight: 600, marginTop: 10 }}>✓ {ok}</div>}
        {err && <div style={{ color: C.red, fontSize: 14, marginTop: 10 }}>⚠ {err}</div>}
      </div>

      <div style={{ display: 'flex', gap: 6, margin: '14px 0 10px', flexWrap: 'wrap' }}>
        {tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} style={{ ...chip(tab === k), height: 38, padding: '0 16px', fontSize: 14 }}>{l}</button>)}
      </div>

      {tab === 'panels' && (!d.panels.length ? <div style={{ color: C.mute, padding: 16 }}>패널이 없습니다 — 사무실에서 작업지시를 내리면 생깁니다.</div>
        : <>
          <div style={{ fontSize: 13, color: C.mute, marginBottom: 8 }}>패널을 누르면 「생산 완료」, 다시 누르면 되돌립니다. 입고된 패널은 바뀌지 않습니다.</div>
          {groups.map(([kind, ps]) => (
            <div key={kind} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 700, margin: '4px 0 8px', color: C.text }}>
                {kind} <span style={{ color: C.mute, fontWeight: 500, fontSize: 13 }}>{ps.filter(p => p.STATE !== 'TODO').length} / {ps.length}장</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))', gap: 8 }}>
                {ps.map(p => {
                  const st = PANEL[p.STATE], fixed = p.STATE !== 'TODO' && p.STATE !== 'MADE';
                  return (
                    <button key={p.PANEL_NO} onClick={() => tap(p)} disabled={fixed || busy === p.PANEL_NO} style={{
                      textAlign: 'left', padding: '12px 12px', borderRadius: 14, cursor: fixed ? 'default' : 'pointer', fontFamily: 'inherit',
                      border: `2px solid ${p.STATE === 'TODO' ? C.line : st.color}`, background: p.STATE === 'TODO' ? '#fff' : `${st.color}14`,
                      opacity: busy === p.PANEL_NO ? 0.5 : 1, minHeight: 92,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <b style={{ fontSize: 20, color: C.text }}>{p.PANEL_NO}</b>
                        <span style={{ fontSize: 18 }}>{p.STATE === 'TODO' ? '⬜' : '✅'}</span>
                      </div>
                      <div style={{ fontSize: 14, color: '#333D4B', marginTop: 4, fontWeight: 600 }}>{p.SIZE} × {p.LENGTH}</div>
                      <div style={{ fontSize: 12, color: C.mute, marginTop: 2 }}>{p.JOINT || ''}</div>
                      <div style={{ fontSize: 12, color: st.color, fontWeight: 700, marginTop: 4 }}>{st.label}{p.LOC ? ` · ${p.LOC}` : ''}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </>)}

      {tab === 'cut' && (
        <div style={{ background: '#fff', borderRadius: 16, padding: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr>{['구분', '폭', '길이', '결합', '수량', '생산', '패널 번호'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>{cut.map(r => (
              <tr key={`${r.kind}${r.size}${r.length}${r.joint}`}>
                <td style={td}>{r.kind}</td>
                <td style={{ ...num, fontWeight: 700 }}>{r.size}</td>
                <td style={num}>{r.length}</td>
                <td style={td}>{r.joint}</td>
                <td style={{ ...num, fontWeight: 700 }}>{r.nos.length}</td>
                <td style={{ ...num, color: r.made === r.nos.length ? C.green : C.orange }}>{r.made}</td>
                <td style={{ ...td, fontSize: 12, color: C.sub }}>{r.nos.join(', ')}</td>
              </tr>
            ))}</tbody>
            <tfoot><tr>
              <td style={{ ...td, fontWeight: 700 }} colSpan={4}>합계</td>
              <td style={{ ...num, fontWeight: 700 }}>{d.panels.length}</td><td style={num}>{doneN}</td><td style={td} />
            </tr></tfoot>
          </table>
        </div>
      )}

      {tab === 'plan' && <div style={{ background: '#fff', borderRadius: 16, padding: 12 }}><PlanImage no={no} has={e.HAS_PLAN_IMG} /></div>}

      {tab === 'log' && (
        <div style={{ background: '#fff', borderRadius: 16, padding: 16 }}>
          <textarea value={memo} onChange={ev => setMemo(ev.target.value)} rows={2} maxLength={500}
            placeholder="메모 (예: W3 문틀 자리 재단 확인 필요, 자재 부족 등)" style={{ ...area, width: '100%', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button style={btn()} disabled={!memo.trim()} onClick={() => run(() => addWorkMemo(no, memo.trim()), '메모를 남겼습니다').then(() => setMemo(''))}>메모 남기기</button>
          </div>
          <div style={{ marginTop: 12 }}>
            {log.length === 0 ? <div style={{ color: C.mute, fontSize: 14 }}>기록이 없습니다</div> : log.map(l => (
              <div key={l.LOG_ID} style={{ padding: '10px 0', borderBottom: `1px solid ${C.fill}`, fontSize: 14 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <b style={{ color: l.ACTION === 'NOTE' ? C.orange : C.text }}>{ACT[l.ACTION] ?? l.ACTION}</b>
                  <span style={{ color: C.mute, fontSize: 12 }}>{fmt(l.REG_DT)} · {l.USER_NM || l.USER_ID || ''}</span>
                </div>
                {l.MEMO && <div style={{ color: '#333D4B', marginTop: 2, whiteSpace: 'pre-wrap' }}>{l.MEMO}</div>}
                {l.PANELS && <div style={{ color: C.sub, fontSize: 12, marginTop: 2 }}>{l.PANELS.split(',').length}장 · {l.PANELS.split(',').slice(0, 30).join(', ')}{l.PANELS.split(',').length > 30 ? ' …' : ''}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/** 생산 화면 전체. 생산 직원은 onClose 없이 (로그아웃·비밀번호만) */
const ProductionBoard: React.FC<{ initial?: string | null; onClose?: () => void; user?: string; onPassword?: () => void; onLogout?: () => void; onNotice?: () => void }> = ({
  initial, onClose, user, onPassword, onLogout, onNotice,
}) => {
  const [rows, setRows] = useState<BoardRow[] | null>(null);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'work' | 'made' | 'all'>('work');
  const [cur, setCur] = useState<string | null>(initial ?? null);
  const [err, setErr] = useState<string | null>(null);
  const load = (query = q) => workBoard(query).then(r => { setRows(r.data); setErr(null); }).catch(e => setErr(errMsg(e)));
  useEffect(() => { load(''); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // 작업지시된 것만, 작업일 → 출고 예정 순
  const jobs = useMemo(() => (rows ?? []).filter(r => r.N > 0)
    .sort((a, b) => (a.WORK_DT || '99999999').localeCompare(b.WORK_DT || '99999999') || (a.DUE_DT || '99999999').localeCompare(b.DUE_DT || '99999999')), [rows]);
  const isMade = (r: BoardRow) => r.MADE >= r.N;
  const shown = jobs.filter(r => tab === 'all' || (tab === 'work' ? !isMade(r) : isMade(r) && r.JOB_STATE !== 'DONE'));
  const narrow = typeof window !== 'undefined' && window.innerWidth < 760;
  const list = (
    <div style={{ width: narrow ? '100%' : 380, borderRight: narrow ? undefined : `1px solid ${C.line}`, background: '#fff', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ display: 'flex', gap: 6, padding: 12, flexWrap: 'wrap', borderBottom: `1px solid ${C.fill}` }}>
        {([['work', '생산 중', jobs.filter(r => !isMade(r)).length], ['made', '생산 완료', jobs.filter(r => isMade(r) && r.JOB_STATE !== 'DONE').length], ['all', '전체', jobs.length]] as const)
          .map(([k, l, n]) => <button key={k} onClick={() => setTab(k)} style={{ ...chip(tab === k), height: 34 }}>{l} {n}</button>)}
      </div>
      {err && <div style={{ color: C.red, padding: 10, fontSize: 13 }}>⚠ {err}</div>}
      <div style={{ overflow: 'auto', flex: 1 }}>
        {rows === null ? <div style={{ padding: 16, color: C.mute }}>불러오는 중…</div>
          : shown.length === 0 ? <div style={{ padding: 16, color: C.mute }}>{tab === 'work' ? '생산할 작업이 없습니다' : '없습니다'}</div>
          : shown.map(r => (
            <div key={r.ESTI_NO} onClick={() => setCur(r.ESTI_NO)} style={{ padding: '14px 16px', borderBottom: `1px solid ${C.fill}`, cursor: 'pointer',
              background: cur === r.ESTI_NO ? C.blueWeak : undefined }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <b style={{ fontSize: 15, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.SITE_NM || '(현장명 없음)'}</b>
                {isMade(r) ? <span style={{ fontSize: 12, color: C.green, fontWeight: 700 }}>✓ 완료</span>
                  : <span style={{ fontSize: 13, color: C.orange, fontWeight: 700 }}>{r.MADE}/{r.N}</span>}
              </div>
              <div style={{ fontSize: 12, color: C.sub, marginTop: 4 }}>
                작업 {d8(r.WORK_DT).slice(5) || '-'} · <span style={{ color: late(r) ? C.red : undefined, fontWeight: late(r) ? 700 : 400 }}>
                  출고 {d8(r.DUE_DT).slice(5) || '-'}{late(r) ? ' 지연' : ''}</span> · {r.PANEL_THICK}T{r.WORK_MGR ? ` · ${r.WORK_MGR}` : ''}
              </div>
              <div style={{ height: 5, borderRadius: 3, background: C.fill, overflow: 'hidden', marginTop: 6 }}>
                <div style={{ width: `${(Math.min(r.MADE, r.N) / r.N) * 100}%`, height: '100%', background: isMade(r) ? C.green : C.orange }} />
              </div>
            </div>
          ))}
      </div>
    </div>
  );
  return (
    <div style={{ position: 'fixed', inset: 0, background: C.bg, zIndex: 1500, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: '#fff', borderBottom: `1px solid ${C.line}`, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {narrow && cur && <button onClick={() => setCur(null)} style={btn('#8B95A1')}>‹ 목록</button>}
        <b style={{ fontSize: 18, color: C.text, letterSpacing: -0.3 }}>🏭 생산</b>
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(q)} placeholder="현장명·견적번호" style={{ ...inp, width: 180 }} />
        <button onClick={() => load(q)} style={btn(C.blue, false)}>검색</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          {user && <span style={{ fontSize: 13, color: C.sub }}>{user}</span>}
          {onNotice && <button onClick={onNotice} style={btn('#8B95A1')}>📢 게시판</button>}
          {onPassword && <button onClick={onPassword} style={btn('#8B95A1')}>내 정보</button>}
          {onLogout && <button onClick={onLogout} style={btn('#F04452', false)}>로그아웃</button>}
          {onClose && <button onClick={onClose} style={btn('#8B95A1')}>닫기</button>}
        </div>
      </div>
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {(!narrow || !cur) && list}
        {(!narrow || cur) && <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {cur ? <Detail no={cur} onChanged={() => load()} /> : <div style={{ padding: 24, color: C.mute }}>왼쪽에서 작업을 고르세요</div>}
        </div>}
      </div>
    </div>
  );
};

export default ProductionBoard;
